import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { showModal } from 'modal.react.js';
import { mergeDeepRight, omit } from 'ramda';
import { useForm, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { areaLoadTypes } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/constants';
import Preview from 'components/Pages/Admin/shared/Preview';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { ETripEvent } from 'detailsv2/structure';
import { itineraryScheduleTypeSelector } from 'store/itinerariesItem/selectors';
import { createOptionFromPageV2 } from 'utils/createOptionFromPage';

import { getOptionsLoader } from '../../../../functions';
import { useDefaultValues, useTripEventSaveInfo, useChangePagePopover } from '../../../shared';
import { useAreaWizardContext } from '../../context';
import * as types from './types';
import { FieldValues } from './types';
import { areaValidationSchema } from './validation';

export const useAreaForm = () => {
  const { tripEvent, data, onSubmit } = useAreaWizardContext();
  const organizedBy = useSelector(itineraryScheduleTypeSelector);

  const filteredStepData = omit(['prevAttachedPage'], data.default); // remove unexpected backend data
  const defaultValues = mergeDeepRight(tripEvent, filteredStepData);
  const defaultAttachedPage = defaultValues?.attachedPages?.[0];

  const methods = useForm<types.FieldValues>({
    defaultValues: {
      ...defaultValues,
      attachedPage: defaultAttachedPage,
    },
    resolver: yupResolver(areaValidationSchema(organizedBy)),
    mode: 'onTouched',
  });

  const { watch } = methods;
  const attachedPage = watch('attachedPage');
  const isVisibleFormBody = !!attachedPage;

  const handleResetData = React.useCallback((currentData: FieldValues, newPage: { name?: string | null }) => {
    return {
      data: {
        isCollapsible: false,
        title: newPage.name ? `Welcome to ${newPage.name}` : '',
        details: newPage.name ? { text: `Today you will arrive in ${newPage.name}`, raw: null } : null,
        notes: null,
      },
    };
  }, []);

  const { handleAttachedPage, keepData, resetData, setProceedStatus } = useDefaultValues({
    methods,
    onResetData: handleResetData,
    eventType: ETripEvent.Area,
  });

  const { onChangePage, popover } = useChangePagePopover({
    keepData,
    resetData,
    setProceedStatus,
    handleAttachedPage,
    methods,
    prevAttachedPage: data.default.prevAttachedPage,
  });

  /* Call onChangePage after chose "one-off event" (or click "back" button) on creation new event in builder */
  React.useEffect(() => {
    if (attachedPage?.isNew) {
      onChangePage(attachedPage.name, attachedPage.id);
    }
  }, []);

  const { saveInfoSubmitHandler, isSafeInfoDisabled, safeInfoTooltipId } = useTripEventSaveInfo<FieldValues>({
    eventType: ETripEvent.Area,
    methods,
    handleSubmit: onSubmit,
  });

  const loadOptions = getOptionsLoader(areaLoadTypes, createOptionFromPageV2);

  const openPagePreview = async (pageId: number) => {
    await showModal(area, (res) => (
      <Modal noPadding size="lg" isOpen toClose={res} stopMouseDownPropagation>
        <Preview pageId={pageId} />
      </Modal>
    ));
  };

  return {
    methods,
    loadOptions,
    openPagePreview,
    isVisibleFormBody,
    onChangePage,
    tripEventDeafultValuesController: {
      isSafeInfoDisabled,
      safeInfoTooltipId,
      saveInfoSubmitHandler,
    },
    popover,
  };
};

export const useAreaFormContext = () => {
  const methods = useFormContext<types.FieldValues>();
  return methods;
};
